<template>
  <dx-modal :value="show" title="Atualizar preços e repasse das guias" size="lg"
            class="gd-scope gd-reprice-modal" id="modal-billing-guides-reprice"
            :closable="!applying" @input="close()">
    <template v-if="result">
      <div class="cr-summary">
        <div class="cr-summary-item cr-summary-scope">
          <small>Resultado</small>
          <div class="cr-summary-value">{{ resultText }}</div>
          <span class="cr-summary-hint" v-if="result.failed > 0">
            {{ result.failed }} sem atualizar
          </span>
        </div>
        <div class="cr-summary-item soft done">
          <small>Atualizadas</small>
          <div class="cr-summary-value">{{ result.updated }}</div>
        </div>
        <div class="cr-summary-item soft">
          <small>Sem atualizar</small>
          <div class="cr-summary-value">{{ result.failed }}</div>
        </div>
      </div>

      <div class="cr-panel">
        <div class="cr-panel-head">
          <div class="cr-panel-title">
            <fa-icon :icon="['fal', 'list']" class="cr-panel-icon"/>
            Resultado por guia
            <span class="label label-rounded">{{ result.results.length }}</span>
          </div>
        </div>
        <div class="cr-table-scroll">
          <table class="table gd-reprice-table">
            <thead>
            <tr>
              <th style="width: 90px">Guia</th>
              <th>Paciente</th>
              <th class="text-center" style="width: 90px">Itens</th>
              <th style="width: 320px">Situação</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="entry in result.results" :key="entry.id">
              <td class="gd-num text-bold">{{ numberOf(entry.id) }}</td>
              <td><span class="gd-ell">{{ patientOf(entry.id) }}</span></td>
              <td class="text-center gd-num">{{ changedOf(entry) }}</td>
              <td>
                <span class="gd-pill gd-pill-closed" v-if="entry.ok">
                  <fa-icon :icon="['fal', 'check-circle']"/>Atualizada
                </span>
                <template v-else>
                  <span class="gd-pill gd-pill-open">
                    <fa-icon :icon="['fal', 'exclamation-triangle']"/>Não atualizada
                  </span>
                  <span class="gd-sub gd-err" v-if="entry.message">{{ entry.message }}</span>
                </template>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="stenci-group stenci-alert gd-reprice-note">
        As guias selecionadas passam a seguir a tabela, o plano e a regra de repasse vigentes
        agora. Confira o de/para antes de aplicar; a alteração fica registrada no histórico
        de cada guia.
      </div>

      <div class="cr-summary">
        <div class="cr-summary-item cr-summary-scope">
          <small>Guias que mudam</small>
          <div class="cr-summary-value">{{ preview.changed }} de {{ preview.accounts.length }}</div>
          <span class="cr-summary-hint" v-if="preview.blocked > 0">
            {{ preview.blocked }} sem poder atualizar
          </span>
        </div>
        <div class="cr-summary-item soft">
          <small>Convênio</small>
          <div class="cr-summary-value">{{ preview.after.insurance | currency }}</div>
          <span class="cr-summary-hint">antes {{ preview.before.insurance | currency }}</span>
        </div>
        <div class="cr-summary-item">
          <small>Diferença no convênio</small>
          <div class="cr-summary-value" :class="diffClass(preview.diff.insurance)">
            {{ signed(preview.diff.insurance) }}
          </div>
        </div>
        <div class="cr-summary-item soft">
          <small>Repasse</small>
          <div class="cr-summary-value">{{ preview.after.transfer | currency }}</div>
          <span class="cr-summary-hint">antes {{ preview.before.transfer | currency }}</span>
        </div>
        <div class="cr-summary-item">
          <small>Diferença no repasse</small>
          <div class="cr-summary-value" :class="diffClass(preview.diff.transfer)">
            {{ signed(preview.diff.transfer) }}
          </div>
        </div>
      </div>

      <div class="cr-panel">
        <div class="cr-panel-head">
          <div class="cr-panel-title">
            <fa-icon :icon="['fal', 'file-medical-alt']" class="cr-panel-icon"/>
            De / para por guia
            <span class="label label-rounded">{{ preview.changed }}</span>
          </div>
          <div class="cr-panel-subtitle">
            Só as guias abertas e sem lote entram; as demais aparecem com o motivo da recusa
          </div>
        </div>
        <div class="cr-table-scroll">
          <table class="table gd-reprice-table">
            <thead>
            <tr>
              <th style="width: 80px">Data</th>
              <th style="width: 90px">Guia</th>
              <th>Paciente</th>
              <th class="text-center" style="width: 60px">Itens</th>
              <th class="text-right" style="width: 100px">Convênio antes</th>
              <th class="text-right" style="width: 100px">Convênio depois</th>
              <th class="text-right" style="width: 100px">Diferença</th>
              <th class="text-right" style="width: 100px">Repasse depois</th>
              <th class="text-right" style="width: 100px">Diferença</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="account in preview.accounts" :key="account.id"
                :class="rowClass(account)">
              <td class="gd-num">{{ account.startDate | date }}</td>
              <td class="gd-num text-bold">{{ account.number || account.code }}</td>
              <td>
                <span class="gd-ell">{{ account.patientName }}</span>
                <span class="gd-sub gd-err" v-if="account.blocked && account.blocked.message">
                  {{ account.blocked.message }}
                </span>
                <span class="gd-sub" v-else-if="!account.changed">sem alteração</span>
              </td>
              <td class="text-center gd-num">
                <template v-if="!account.blocked">{{ account.changed }}</template>
                <span class="gd-muted" v-else>—</span>
              </td>
              <template v-if="account.blocked">
                <td class="text-right gd-muted" colspan="5">Não entra nesta atualização</td>
              </template>
              <template v-else>
                <td class="text-right gd-num">{{ account.before.insurance | currency }}</td>
                <td class="text-right gd-val">{{ account.after.insurance | currency }}</td>
                <td class="text-right gd-val" :class="diffClass(account.diff.insurance)">
                  {{ signed(account.diff.insurance) }}
                </td>
                <td class="text-right gd-val">{{ account.after.transfer | currency }}</td>
                <td class="text-right gd-val" :class="diffClass(account.diff.transfer)">
                  {{ signed(account.diff.transfer) }}
                </td>
              </template>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <template slot="footer">
      <template v-if="!result">
        <button class="btn btn-primary btn-icon btn-icon-left mr-1"
                :class="{loading: applying}"
                :disabled="applying || eligible.length === 0"
                @click="$emit('confirm')">
          <fa-icon :icon="['fal', 'sync']"/>Atualizar {{ eligible.length }}
          {{ eligible.length === 1 ? 'guia' : 'guias' }}
        </button>
        <button class="btn" :disabled="applying" @click="close()">Voltar</button>
      </template>
      <button class="btn btn-primary" v-else @click="close()">Fechar</button>
    </template>
  </dx-modal>
</template>

<script>
import currency from '@/filters/currency';

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    preview: {
      type: Object,
      required: true,
    },
    applying: {
      type: Boolean,
      default: false,
    },
    result: {
      type: Object,
      default: null,
    },
  },
  computed: {
    eligible() {
      return this.preview.accounts.filter(account => !account.blocked && account.changed > 0);
    },
    resultText() {
      const { updated } = this.result;
      if (updated === 0) return 'Nenhuma guia atualizada';
      return `${updated} ${updated === 1 ? 'guia atualizada' : 'guias atualizadas'}`;
    },
  },
  methods: {
    rowClass(account) {
      return {
        unchanged: !account.changed && !account.blocked,
        blocked: !!account.blocked,
      };
    },
    rowOf(id) {
      return this.preview.accounts.find(account => account.id === id) || null;
    },
    numberOf(id) {
      const row = this.rowOf(id);
      if (!row) return '';
      return row.number || row.code || '';
    },
    changedOf(entry) {
      if (entry.changed > 0) return entry.changed;
      const row = this.rowOf(entry.id);
      return row ? row.changed : 0;
    },
    patientOf(id) {
      const row = this.rowOf(id);
      return row ? row.patientName : '';
    },
    signed(value) {
      const amount = Number(value) || 0;
      if (amount === 0) return '—';
      return `${amount > 0 ? '+' : '-'} ${currency(Math.abs(amount))}`;
    },
    diffClass(value) {
      const amount = Number(value) || 0;
      return {
        'gd-up': amount > 0,
        'gd-down': amount < 0,
        'gd-muted': amount === 0,
      };
    },
    close() {
      if (this.applying) return;
      this.$emit('close');
    },
  },
};
</script>
